/**
 * Making real team colors usable on a dark UI.
 *
 * Roughly a third of the team library has a primary color that is black or
 * near-black -- the Raiders, Steelers, Army, Vanderbilt, Texans and ~45 others
 * in FBS alone. Painted straight onto the app's #0a0e14 background those teams
 * are invisible: the banner gradient goes flat and the axis headers lose their
 * team identity entirely.
 *
 * Swapping in ESPN's `alternateColor` was the obvious fix and does not work --
 * measured across the full library it rescues only about a quarter of cases,
 * because many teams list black for both.
 *
 * So the stored color stays the true brand color, and the *display* color is
 * derived here: lightened just far enough to clear a contrast floor against
 * whatever it sits on. Deterministic, works for every team, and preserves hue
 * wherever there is one (black has none, so it lands on a neutral gray).
 *
 * Pure functions over sRGB hex. No DOM, no CSS.
 */

/** The app's page background. Default reference for contrast checks. */
export const APP_BACKGROUND = '#0a0e14';

/**
 * Minimum contrast ratio for a team color used as a large block of fill.
 *
 * This is a visual-distinctness floor, not a WCAG text threshold -- these are
 * decorative surfaces, not text. Text drawn on top is handled by `onColor`,
 * which does target a real readability ratio.
 */
export const MIN_FILL_CONTRAST = 1.9;

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** Parses #rgb or #rrggbb. Returns null on anything else. */
export function parseHex(hex: string): Rgb | null {
  const value = hex.trim().replace(/^#/, '');
  const full =
    value.length === 3
      ? value
          .split('')
          .map((c) => c + c)
          .join('')
      : value;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return {
    r: Number.parseInt(full.slice(0, 2), 16),
    g: Number.parseInt(full.slice(2, 4), 16),
    b: Number.parseInt(full.slice(4, 6), 16),
  };
}

export function toHex({ r, g, b }: Rgb): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  return (
    '#' +
    [r, g, b].map((n) => clamp(n).toString(16).padStart(2, '0')).join('')
  );
}

/** WCAG relative luminance. */
export function relativeLuminance(color: Rgb): number {
  const channel = (raw: number) => {
    const v = raw / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return (
    0.2126 * channel(color.r) + 0.7152 * channel(color.g) + 0.0722 * channel(color.b)
  );
}

/** WCAG contrast ratio between two colors. Always >= 1. */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Moves a color toward white by `amount` (0-1). */
function lighten(color: Rgb, amount: number): Rgb {
  return {
    r: color.r + (255 - color.r) * amount,
    g: color.g + (255 - color.g) * amount,
    b: color.b + (255 - color.b) * amount,
  };
}

/**
 * A team color safe to paint on `background`.
 *
 * Returns the original when it already has enough contrast -- the common case,
 * and the one where brand fidelity matters most. Otherwise lightens toward
 * white in small steps, stopping at the first value that clears `minContrast`.
 *
 * Stepwise rather than solved analytically because the sRGB transfer curve
 * makes the closed form fiddly, and 20 steps is imperceptible cost for a value
 * computed once per render.
 */
export function readableTeamColor(
  color: string,
  background: string = APP_BACKGROUND,
  minContrast: number = MIN_FILL_CONTRAST,
): string {
  const base = parseHex(color);
  const bg = parseHex(background);
  if (!base || !bg) return color;
  if (contrastRatio(base, bg) >= minContrast) return color;

  const STEPS = 20;
  for (let i = 1; i <= STEPS; i++) {
    const candidate = lighten(base, i / STEPS);
    if (contrastRatio(candidate, bg) >= minContrast) return toHex(candidate);
  }
  return '#ffffff';
}

// ---------------------------------------------------------------------------
// Telling two teams apart
// ---------------------------------------------------------------------------

/**
 * CIE L*a*b*, via linear sRGB and XYZ under a D65 white point.
 *
 * Needed because "are these two team colors too alike?" is a perceptual
 * question, and WCAG contrast cannot answer it -- contrast is luminance only,
 * so it rates a mid red and a mid green as near-identical when nobody would
 * confuse them. Lab puts hue and lightness in the same space.
 */
export function rgbToLab({ r, g, b }: Rgb): { L: number; a: number; bb: number } {
  const lin = (v: number) => {
    const c = v / 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const [R, G, B] = [lin(r), lin(g), lin(b)];

  // sRGB -> XYZ (D65), then normalised by the reference white.
  const X = (0.4124 * R + 0.3576 * G + 0.1805 * B) / 0.95047;
  const Y = 0.2126 * R + 0.7152 * G + 0.0722 * B;
  const Z = (0.0193 * R + 0.1192 * G + 0.9505 * B) / 1.08883;

  const f = (t: number) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  const [fx, fy, fz] = [f(X), f(Y), f(Z)];

  return { L: 116 * fy - 16, a: 500 * (fx - fy), bb: 200 * (fy - fz) };
}

/**
 * CIE76 colour difference. Roughly: under ~10 most people call two colours the
 * same, over ~40 nobody would.
 *
 * CIE76 rather than CIE94/2000 because the extra accuracy of the later
 * formulae buys nothing at the coarse "can a person tell these apart across a
 * room" threshold this is used for.
 */
export function deltaE(a: string, b: string): number {
  const ra = parseHex(a);
  const rb = parseHex(b);
  if (!ra || !rb) return Number.POSITIVE_INFINITY;
  const la = rgbToLab(ra);
  const lb = rgbToLab(rb);
  return Math.sqrt(
    (la.L - lb.L) ** 2 + (la.a - lb.a) ** 2 + (la.bb - lb.bb) ** 2,
  );
}

/**
 * Chroma: how far a colour is from grey. A near-grey has no meaningful hue, so
 * hue distance tells you nothing about it.
 */
export function chroma(color: string): number {
  const rgb = parseHex(color);
  if (!rgb) return 0;
  const { a, bb } = rgbToLab(rgb);
  return Math.hypot(a, bb);
}

/** Hue angle in degrees, 0-360. */
export function hueAngle(color: string): number {
  const rgb = parseHex(color);
  if (!rgb) return 0;
  const { a, bb } = rgbToLab(rgb);
  const deg = (Math.atan2(bb, a) * 180) / Math.PI;
  return deg < 0 ? deg + 360 : deg;
}

/** Shortest distance between two hue angles, 0-180. */
export function hueDifference(a: string, b: string): number {
  const diff = Math.abs(hueAngle(a) - hueAngle(b));
  return Math.min(diff, 360 - diff);
}

/**
 * Two colours of the same family at the same brightness read as one colour,
 * whatever the overall distance says.
 *
 * Florida State garnet against SMU red measures dE 50 -- twice the distinctness
 * floor -- yet looks like two reds, because almost all of that distance is
 * saturation. Their hues are 30 degrees apart and their lightness differs by 4.
 * Pairs nobody would confuse sit past 110 degrees.
 *
 * Below this chroma a colour is near-grey and its hue angle is noise, so the
 * hue rule is skipped for it.
 */
export const MIN_MATCHUP_HUE = 45;
export const MIN_MATCHUP_LIGHTNESS = 25;
const GREY_CHROMA = 12;

/**
 * Below this, two teams painted side by side read as the same colour.
 *
 * Calibrated against the real NFL palette: at 25, 81 of the 496 possible
 * matchups clash and every one of them is fixable by an alternate. Obviously
 * distinct pairings sit near 100, nowhere close to firing.
 *
 * Worth knowing how bad the clashes get: ESPN currently lists New England,
 * Seattle and Dallas at the identical #002a5c, so those pairings differ by
 * exactly zero.
 */
export const MIN_MATCHUP_DELTA = 25;

export interface TeamColorChoice {
  /** The team's own primary and alternate, in that preference order. */
  primary: string;
  alternate?: string;
}

export interface MatchupColors {
  home: string;
  away: string;
  /** True when either side had to fall back to its alternate. */
  swapped: boolean;
}

/**
 * Ranks two candidate colour pairings.
 *
 * In order: clearing the distinctness bar beats not clearing it; then fewer
 * swaps away from the primaries; then, among equals, the pairing that
 * separates the two teams most. That last tie-break is why a Patriots/Seahawks
 * board lands on Seattle's action green rather than merely the first
 * combination that happened to work.
 */
function better(
  candidate: { delta: number; cost: number; clears: boolean },
  incumbent: { delta: number; cost: number; clears: boolean },
): boolean {
  const candidateClears = candidate.clears;
  const incumbentClears = incumbent.clears;
  if (candidateClears !== incumbentClears) return candidateClears;
  if (!candidateClears) return candidate.delta > incumbent.delta;
  if (candidate.cost !== incumbent.cost) return candidate.cost < incumbent.cost;
  return candidate.delta > incumbent.delta;
}

/**
 * Chooses the colour each side is drawn in so the two are tellable apart.
 *
 * Primaries win whenever they are already distinct -- brand fidelity is the
 * default and most matchups need nothing. When they clash, each team's own
 * alternate is tried: Seattle's action green against New England's navy is the
 * canonical case, and an alternate is a real brand colour rather than a
 * computed shift, so the team still looks like itself.
 *
 * If nothing clears the threshold -- rare; across the NFL every clashing pair
 * is fixable -- the most distinguishable combination found is used, which is
 * more use than keeping two identical colours. The admin can always override.
 */
/**
 * Whether two painted colours would read as the same one.
 *
 * Distance alone is not enough: it conflates saturation with hue, so it rates
 * a deep garnet and a bright red as distinct. The second test catches same
 * family, same brightness.
 */
export function tooAlike(a: string, b: string): boolean {
  if (deltaE(a, b) < MIN_MATCHUP_DELTA) return true;
  if (chroma(a) < GREY_CHROMA || chroma(b) < GREY_CHROMA) return false;
  return (
    hueDifference(a, b) < MIN_MATCHUP_HUE &&
    Math.abs(lightness(a) - lightness(b)) < MIN_MATCHUP_LIGHTNESS
  );
}

/** L* of a colour, 0-100. */
function lightness(color: string): number {
  const rgb = parseHex(color);
  return rgb ? rgbToLab(rgb).L : 0;
}

export function pickMatchupColors(
  home: TeamColorChoice,
  away: TeamColorChoice,
): MatchupColors {
  const homeOptions = [home.primary, home.alternate].filter(
    (c): c is string => Boolean(c) && parseHex(c!) !== null,
  );
  const awayOptions = [away.primary, away.alternate].filter(
    (c): c is string => Boolean(c) && parseHex(c!) !== null,
  );

  if (homeOptions.length === 0 || awayOptions.length === 0) {
    return { home: home.primary, away: away.primary, swapped: false };
  }

  let best:
    | { home: string; away: string; delta: number; cost: number; clears: boolean }
    | null = null;

  for (const [hi, h] of homeOptions.entries()) {
    for (const [ai, a] of awayOptions.entries()) {
      // Compare what actually gets painted: both are lightened for the dark UI
      // before display, and that shifts them.
      const paintedHome = readableTeamColor(h);
      const paintedAway = readableTeamColor(a);
      const delta = deltaE(paintedHome, paintedAway);
      const clears = !tooAlike(paintedHome, paintedAway);
      // Prefer leaving primaries alone; each swap costs one.
      const cost = hi + ai;
      if (!best || better({ delta, cost, clears }, best)) {
        best = { home: h, away: a, delta, cost, clears };
      }
    }
  }

  const chosen = best!;
  return {
    home: chosen.home,
    away: chosen.away,
    swapped: chosen.home !== home.primary || chosen.away !== away.primary,
  };
}

/**
 * Black or white, whichever is readable as text/iconography drawn *on* the
 * given color.
 *
 * The monogram badges previously hardcoded white, which is unreadable on the
 * lighter team colors (New Orleans' gold, Wyoming's, Georgia Tech's).
 */
export function onColor(color: string): '#000000' | '#ffffff' {
  const base = parseHex(color);
  if (!base) return '#ffffff';
  const white = { r: 255, g: 255, b: 255 };
  const black = { r: 0, g: 0, b: 0 };
  return contrastRatio(base, white) >= contrastRatio(base, black)
    ? '#ffffff'
    : '#000000';
}
